// useSpotlight — tiny pub/sub for triggering spotlight tours from anywhere.
//
// Usage:
//   const { startTour, hasSeen, markSeen } = useSpotlight();
//   startTour(TOUR_IDS.MOMENTUM);                  // shows it (skips if seen)
//   startTour(TOUR_IDS.MOMENTUM, { force: true }); // re-runs even if seen
//
// localStorage flags persist "seen" state per device. Server-side persistence
// can be layered on later without changing this API.

import { useCallback, useEffect, useState } from "react";

const SEEN_PREFIX = "wing.spotlight.seen.";

let listeners = new Set();
let activeTour = null;          // { id, force } | null

const notify = () => listeners.forEach((fn) => fn(activeTour));

export function startTour(id, { force = false } = {}) {
    activeTour = { id, force, ts: Date.now() };
    notify();
}

export function endTour() {
    activeTour = null;
    notify();
}

export function hasSeen(id) {
    try { return localStorage.getItem(SEEN_PREFIX + id) === "1"; }
    catch { return false; }
}

export function markSeen(id) {
    try { localStorage.setItem(SEEN_PREFIX + id, "1"); } catch { /* quota / private mode */ }
}

export function resetSeen(id) {
    try { localStorage.removeItem(SEEN_PREFIX + id); } catch { /* noop */ }
}

export function useSpotlight() {
    const [tour, setTour] = useState(activeTour);
    useEffect(() => {
        const fn = (t) => setTour(t);
        listeners.add(fn);
        return () => { listeners.delete(fn); };
    }, []);
    return {
        tour,
        startTour: useCallback(startTour, []),
        endTour: useCallback(endTour, []),
        hasSeen,
        markSeen,
        resetSeen,
    };
}
